import { useEffect, useMemo, useState } from 'react';
import { useLang } from '../../../../../hooks/use-lang';
import { useQueryParams } from '../../../../../hooks/use-query-params';
import { useEmployeesStore } from '../../../../../store/use-employee-store';
import { validate } from '../../../../../helpers/validate';
import { phoneRegex } from '../../../../../helpers/regexp';
import { validateLang } from '../../../../../helpers/lang';
import { EmployeeType } from '../../../../../store/api/employees-api';
import { useImageStore } from '../../../../../store/use-image-store';
import { IdValue } from '../../../../../types/props';
import { getBaseUrl } from '../../../../../store/api/utils.ts/stand';

export const useEmployeeProfile = () => {
  const params = useQueryParams().getQueryParams();
  const { t, getLocale } = useLang();
  const { fetchEmployeeById, employee, putEmployee, isLoadingFetch } = useEmployeesStore();
  const { postImage, isLoading: isLoadingImage } = useImageStore();

  const employeeId = params?.employeeId;

  const [updatedEmployee, setUpdatedEmployee] = useState<Partial<EmployeeType>>();

  const [qr, setQr] = useState<IdValue>();

  useEffect(() => {
    if (employeeId) {
      fetchEmployeeById(employeeId).then((resp) => {
        if (resp) {
          setUpdatedEmployee(resp);
          const initQr = resp?.profile?.available_qrs?.[0];
          if (initQr) {
            const idVal = { id: initQr.id, value: getLocale(initQr.value) };
            setQr(idVal);
          }
        }
      });
    }
  }, [employeeId]);

  const handleSave = () => {
    const isValid = validate({
      patronymic: { rule: () => !!validateLang(updatedEmployee?.patronymic) },
      phone: { rule: (val) => !!val && phoneRegex.test(val) },
      display_title: { rule: () => !!validateLang(updatedEmployee?.profile?.display_title) },
    });
    if (!isValid) return;
    if (updatedEmployee) {
      putEmployee(updatedEmployee);
    }
  };

  const employeeUrl =
    qr?.id && updatedEmployee?.id
      ? `${getBaseUrl()}/qr/${qr.id}/employee?id=${updatedEmployee.id}`
      : '';

  const isChanged = useMemo(() => {
    if (!employee || !updatedEmployee) return false;
    const stringifiedInitial = JSON.stringify(employee);
    const stringifiedUpdated = JSON.stringify(updatedEmployee);
    return stringifiedInitial !== stringifiedUpdated;
  }, [employee, updatedEmployee]);

  const handleUpdateEmployee = (data: Partial<EmployeeType>) => {
    setUpdatedEmployee((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const handleUploadImage = (file: File[]) => {
    const formData = new FormData();
    formData.append('image', file[0]);
    postImage(formData).then((res) => {
      if (res) {
        setUpdatedEmployee((prev) => ({
          ...prev,
          profile: { ...prev?.profile, avatar: res },
        }));
      }
    });
  };

  return {
    t,
    employee,
    updatedEmployee,
    employeeId,
    handleUpdateEmployee,
    isLoading: isLoadingFetch || isLoadingImage,
    handleSave,
    handleUploadImage,
    qr,
    setQr,
    isChanged,
    employeeUrl,
  };
};
